import { useState, useRef, useEffect } from "react";
import { X, Sparkles, Plus } from "lucide-react";
import { useParams } from "react-router-dom";
import { toastify } from "../Toast";
import axios from "axios";

// Button Component
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  disabled = false,
  onClick,
}) => {
  const baseStyles =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50";

  const variants = {
    default: "bg-orange-500 text-white hover:bg-orange-600",
    outline: "border border-gray-300 bg-white hover:bg-gray-50 text-gray-700",
    ghost: "hover:bg-gray-100 text-gray-700",
  };

  const sizes = {
    default: "h-10 px-4 py-2",
    sm: "h-8 px-3 text-sm",
    lg: "h-11 px-8",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </button>
  );
};

// Label Component
const Label = ({ children, htmlFor, className = "" }) => (
  <label
    htmlFor={htmlFor}
    className={`text-sm font-medium leading-none ${className}`}
  >
    {children}
  </label>
);
const Textarea = ({
  value,
  onChange,
  onPaste,
  placeholder,
  className = "",
  id,
}) => (
  <textarea
    id={id}
    value={value}
    onChange={onChange}
    onPaste={onPaste}
    placeholder={placeholder}
    className={`w-full rounded-md border px-3 py-2 focus:outline-none ${className}`}
    style={{
      backgroundClip: "padding-box",
      backgroundImage: "none",
      backgroundAttachment: "local",
      // Additional styles for resizer
      scrollbarWidth: "none", // Firefox
      msOverflowStyle: "none", // IE/Edge
    }}
  />
);
const ImageEditComponent = ({
  isOpen = false,
  imageUrl = "",
  onClose = () => { },
  fetchAdDetail,
  existingVariationUrls = [],
}) => {
  const { id } = useParams();
  const [editInstructions, setEditInstructions] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState("1:1");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (imageUrl) {
      const img = new window.Image();
      img.src = typeof imageUrl === "object" && imageUrl !== null ? imageUrl.url : imageUrl;
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        let ratioId = "1:1";

        const ratio = w / h;
        if (Math.abs(ratio - 1) < 0.05) ratioId = "1:1";
        else if (Math.abs(ratio - (9 / 16)) < 0.05) ratioId = "9:16";
        else if (Math.abs(ratio - (16 / 9)) < 0.05) ratioId = "16:9";
        else if (Math.abs(ratio - (4 / 5)) < 0.05) ratioId = "4:5";

        setSelectedAspectRatio(ratioId);
      };
    }
  }, [imageUrl]);

  const aspectRatios = [
    { id: "1:1", value: "1:1", label: "Square (1:1)" },
    { id: "9:16", value: "9:16", label: "Vertical (9:16)" },
    { id: "16:9", value: "16:9", label: "Wide (16:9)" },
    { id: "4:5", value: "4:5", label: "Portrait (4:5)" },
  ];

  // Aspect ratio icon component
  const AspectRatioIcon = ({ ratio }) => {
    switch (ratio) {
      case "1:1":
      case "1024x1024":
        return (
          <svg
            className="w-4 h-4 text-gray-500"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="3" y="3" width="10" height="10" rx="1" />
          </svg>
        );
      case "9:16":
      case "1024x1536":
        return (
          <svg
            className="w-4 h-4 text-gray-500"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="5" y="2" width="6" height="12" rx="1" />
          </svg>
        );
      case "16:9":
      case "1536x1024":
        return (
          <svg
            className="w-4 h-4 text-gray-500"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="2" y="5" width="12" height="6" rx="1" />
          </svg>
        );
      case "4:5":
        return (
          <svg
            className="w-4 h-4 text-gray-500"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="4" y="2" width="8" height="10" rx="1" />
          </svg>
        );
      default:
        return null;
    }
  };

  const handleAspectRatioChange = (ratio) => {
    setSelectedAspectRatio(ratio);
    setIsDropdownOpen(false);
  };

  // Handle file selection
  const handleFileSelect = (files) => {
    const validFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (validFiles.length > 0) {
      const newImages = [];
      let loadedCount = 0;

      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          newImages.push({
            id: Date.now() + Math.random(),
            file: file,
            preview: e.target.result,
            name: file.name,
          });
          loadedCount++;
          if (loadedCount === validFiles.length) {
            setUploadedImages((prev) => {
              const combined = [...prev, ...newImages];
              if (combined.length > 4) {
                toastify("error", "Maximum 4 images allowed. Keeping the first 4.");
              }
              return combined.slice(0, 4);
            });
          }
        };
        reader.readAsDataURL(file);
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  // In your main component - use the enhanced handlePaste:
  const handlePaste = (e) => {
    // Prevent default to stop unwanted text insertion on macOS
    e.preventDefault();

    const items = e.clipboardData?.items;
    if (!items) return;

    let hasImage = false;

    // Look for image data
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        hasImage = true;
        const file = items[i].getAsFile();
        if (file) {
          handleFileSelect([file]);
        }
      }
    }

    // If no image found, handle text paste
    if (!hasImage) {
      const text = e.clipboardData.getData("text/plain");
      if (text) {
        const textarea = e.target;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue =
          editInstructions.substring(0, start) +
          text +
          editInstructions.substring(end);
        setEditInstructions(newValue);

        // Restore cursor position and scroll to bottom
        setTimeout(() => {
          textarea.selectionStart = start + text.length;
          textarea.selectionEnd = start + text.length;
          // Scroll to bottom to show the last line
          textarea.scrollTop = textarea.scrollHeight;
        }, 0);
      }
    }
  };
  // Remove uploaded image
  const removeImage = (imageId) => {
    setUploadedImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleApply = async () => {
    try {
      setIsProcessing(true);
      toastify(
        "success",
        <>
          <p>
            Generating…
            <br />
            Come back later if you’d like.
          </p>
        </>
      );
      const formData = new FormData();

      // Required fields
      const s3Url = imageUrl?.url ? imageUrl?.url : imageUrl;
      formData.append("imageVariationUrl", s3Url);
      formData.append("userPrompt", editInstructions);
      formData.append("size", selectedAspectRatio);
      formData.append("adId", id || "");

      // Optional: uploaded images
      uploadedImages.forEach((img) => {
        formData.append("referenceImage", img.file);
      });

      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_API}ads/edit-image/variation`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await fetchAdDetail(existingVariationUrls);
      setIsProcessing(false);
      setUploadedImages([]);
      setEditInstructions("");
      toastify("success", "Changes applied successfully");
      // handleClosePanel();
    } catch (error) {
      console.error("Error in handleApply:", error);
      toastify("error", "Failed to apply changes");
      setIsProcessing(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="p-4 bg-gray-50 dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg space-y-4 w-full">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-gray-900 dark:text-[#fff]">
          Edit Variation
        </h4>
      </div>

      {/* Instructions with Image Upload */}
      <div className="space-y-2">
        <Label
          htmlFor="edit-instructions"
          className="text-xs font-semibold text-gray-900 dark:text-[#fff]"
        >
          What would you like to change? <span className="text-red-500">*</span>
        </Label>
        <div
          className={`relative rounded-md transition-colors border border-gray-300 dark:border-gray-600`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Tags for uploaded images */}
          {uploadedImages.length > 0 && (
            <div className="flex flex-wrap gap-2 p-2 bg-white dark:bg-transparent border-gray-200 dark:border-gray-700">
              {uploadedImages.map((img) => (
                <div
                  key={img.id}
                  className="inline-flex items-center gap-0.5 bg-gray-200 dark:bg-gray-500 rounded-md px-1 h-[22px]"
                >
                  <img
                    src={img.preview}
                    alt={img.name}
                    className="w-4 h-4 rounded object-cover"
                  />
                  <span className="text-[10px] text-gray-700 dark:text-gray-300 max-w-[40px] truncate leading-none">
                    {img.name}
                  </span>
                  <button
                    onClick={() => removeImage(img.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors leading-none"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Textarea */}
          <Textarea
            ref={textareaRef}
            id="edit-instructions"
            value={editInstructions}
            onChange={(e) => setEditInstructions(e.target.value)}
            onPaste={handlePaste}
            placeholder="e.g., Change the background to blue, make the text larger, add a person smiling... (You can also drag & drop an image here)"
            className={`min-h-[100px] mb-[-5px] ${uploadedImages?.length > 0 && "pt-0"
              } resize-y overflow-auto text-sm bg-white dark:bg-transparent text-gray-900 dark:text-white border-0  [background-clip:padding-box]`}
          />

          {/* Upload Button */}
          <div className="absolute bottom-2 right-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
              multiple
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              title="Upload image"
            >
              <Plus className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Helper text */}
      </div>

      <div className="space-y-2 size-dropdown">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Size <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <div
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-left flex items-center gap-3 text-gray-900 dark:text-[#fff] cursor-pointer"
          >
            <AspectRatioIcon ratio={selectedAspectRatio} />
            <span>
              {aspectRatios.find((r) => r.id === selectedAspectRatio)?.label}
            </span>
            <svg
              className="w-4 h-4 text-gray-400 dark:text-gray-500 ml-auto right-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#0A0A0A] border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10">
              <div className="p-2">
                {aspectRatios.map((ratio) => (
                  <button
                    key={ratio.value}
                    type="button"
                    onClick={() => handleAspectRatioChange(ratio.id)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded flex items-center gap-3 text-sm text-gray-900 dark:text-[#fff]"
                  >
                    <AspectRatioIcon ratio={ratio.id} />
                    <span>{ratio.label}</span>
                    {selectedAspectRatio === ratio.id && (
                      <svg
                        className="w-4 h-4 text-green-500 ml-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Button
        onClick={handleApply}
        disabled={isProcessing || !editInstructions.trim()}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <svg
              className="w-4 h-4 animate-spin"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582M20 20v-5h-.581M5 19A9 9 0 1119 5A9 9 0 015 19z"
              />
            </svg>
            Processing
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            Generate Variations
          </>
        )}
      </Button>
    </div>
  );
};

export default ImageEditComponent;
